import React, { useState, useEffect } from 'react';
import { styled, css } from '@compiled/react';
import DynamicTable from '@atlaskit/dynamic-table';

import Heading from '@atlaskit/heading';

import { ModalTransition } from '@atlaskit/modal-dialog';

import { token } from '@atlaskit/tokens';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { ProjectId } from '@atlassian/jira-shared-types/src/general.tsx';
import { IssueTableSkeleton } from '@atlassian/jira-skeletons/src/ui/cycle-time-breakdown-report/HistoricalCycleTimeSkeleton.tsx';
import ErrorRetry from '@atlassian/jira-software-devops-report-common-error-retry/src/index.tsx';
import IssueModal from '../../common/ui/issue-modal/index.tsx';
import { useSelectedRange } from '../../controllers/selected-date-range/index.tsx';
import { useTableFilterStore } from '../../controllers/top-filter/index.tsx';
import { ViewCycleTimeReportExperienceSuccessTracker } from '../../services/experience-tracker/index.tsx';
import { useProjectId } from '../../services/get-project/index.tsx';
import { useIssueTableData } from '../../services/issue-table/index.tsx';
import messages from './messages.tsx';
import { useTableRows, useTableHeader, countRowsExceedingCycleTime } from './utils.tsx';

export const IssueTable = () => {
	const projectId: ProjectId | null = useProjectId();
	const [selectedDateRange] = useSelectedRange();
	const [filters] = useTableFilterStore();
	const { data, error, loading, fetch } = useIssueTableData(projectId, selectedDateRange, filters);
	const [selectedIssueKey, setSelectedIssueKey] = useState<unknown>(null);
	const rows = useTableRows(data?.rows, data?.cycleTimeStatistic ?? null, setSelectedIssueKey);
	const isEmptyState = rows.length === 0;
	const header = useTableHeader();
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		if (data) {
			fireTrackAnalytics(
				createAnalyticsEvent({}),
				'cycleTimeCounts measured',
				countRowsExceedingCycleTime(data),
			);
		}
	}, [data, createAnalyticsEvent]);

	if ((error && !data) || (!loading && !data)) {
		return (
			<ErrorUIWrapper>
				<ErrorRetry onRetry={fetch} />
			</ErrorUIWrapper>
		);
	}

	// if loading && data is truthy, then let the table handle loading. Display skeleton loading only for the initial load.
	if (loading && !data) {
		return (
			<IssueTableContainer>
				<IssueTableSkeleton />
			</IssueTableContainer>
		);
	}

	return (
		<>
			{/* Experience tracker is located here because when table is ready with data, we know all the services are ready with data. */}
			<ViewCycleTimeReportExperienceSuccessTracker />
			<IssueTableContainer faded={isEmptyState}>
				<Heading size="xsmall" as="h4">
					{formatMessage(messages.title)}
				</Heading>
				{/* eslint-disable-next-line jira/integration/enforce-data-testid-usage */}
				<TableContainer data-test-id="software-cycle-time-breakdown-report.ui.issue-table.table-container">
					<DynamicTable
						key={JSON.stringify(selectedDateRange)}
						head={header}
						rows={rows}
						rowsPerPage={10}
						defaultPage={1}
						loadingSpinnerSize="large"
						isLoading={loading}
						isFixedSize
						defaultSortKey="cycleTime"
						defaultSortOrder="DESC"
					/>
				</TableContainer>
			</IssueTableContainer>
			<ModalTransition>
				{/* @ts-expect-error - TS2769 - No overload matches this call. */}
				{selectedIssueKey && (
					<IssueModal
						// @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'string'.
						issueKey={selectedIssueKey}
						onClose={() => {
							setSelectedIssueKey(null);
						}}
						onIssueKeyChange={({ toIssueKey }) => {
							setSelectedIssueKey(toIssueKey);
						}}
					/>
				)}
			</ModalTransition>
		</>
	);
};

const marginBetweenTableTitleStyles = css({
	marginTop: token('space.200', '16px'),
});

const issueTableMarginTopStyles = css({
	marginTop: token('space.200', '16px'),
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const IssueTableContainer = styled.div<{ faded?: any }>(issueTableMarginTopStyles, {
	paddingTop: 0,
	paddingRight: token('space.100', '8px'),
	paddingBottom: 0,
	paddingLeft: token('space.100', '8px'),
	// eslint-disable-next-line @typescript-eslint/no-explicit-any, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	opacity: (props: any) => (props.faded ? 0.5 : 1),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 75}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const TableContainer = styled.div(marginBetweenTableTitleStyles);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ErrorUIWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 64.5}px`,
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
});
